import { parseDate } from "@internationalized/date";
import { useDateFormatter } from "@react-aria/i18n";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  AlertTriangleIcon,
  CheckCircleIcon,
  LoaderCircleIcon,
} from "lucide-react";
import { useEffect } from "react";
import { match } from "ts-pattern";

import { Alert } from "@/components/base/alert";
import { button } from "@/components/base/button";
import { title } from "@/components/base/primitives";
import { ProfileName } from "@/components/profiles/name";
import { useMembershipPrice } from "@/components/registrations/context";
import { clearSavedCart } from "@/components/registrations/saved-cart";
import { getTournamentDivisionDisplay } from "@/hooks/tournament";
import { useTRPC } from "@/integrations/trpc/react";
import { DefaultLayout } from "@/layouts/default";
import { getDefaultTimeZone } from "@/lib/dates";

export const Route = createFileRoute(
  "/account/registrations/complete/$paymentIntentId",
)({
  head: () => ({
    meta: [{ title: "Checkout" }],
  }),
  component: RouteComponent,
});

function RouteComponent() {
  const { paymentIntentId } = Route.useParams();

  return <PaymentIntentSuccess paymentIntentId={Number(paymentIntentId)} />;
}

// ── Payment Intent polling flow ──

function PaymentIntentSuccess({
  paymentIntentId,
}: {
  paymentIntentId: number;
}) {
  const trpc = useTRPC();

  const { data: intentStatus } = useQuery({
    ...trpc.payments.getPaymentIntentStatus.queryOptions({
      paymentIntentId,
    }),
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (
        status === "completed" ||
        status === "failed" ||
        status === "expired"
      ) {
        return false;
      }
      return 2000;
    },
  });

  const status = intentStatus?.status ?? "pending";

  useEffect(() => {
    if (status === "completed") {
      clearSavedCart();
    }
  }, [status]);

  return match(status)
    .with("pending", "authorized", "fulfilling", () => <ProcessingView />)
    .with("completed", () => (
      <CompletedIntentView invoiceId={intentStatus?.invoiceId ?? null} />
    ))
    .with("failed", "expired", () => (
      <FailedIntentView errorMessage={intentStatus?.errorMessage ?? null} />
    ))
    .exhaustive();
}

function ProcessingView() {
  return (
    <DefaultLayout>
      <div className="mx-auto flex max-w-xl flex-col space-y-6 py-8 text-center">
        <div className="flex justify-center">
          <LoaderCircleIcon className="h-16 w-16 animate-spin text-blue-500" />
        </div>
        <h1 className={title({ size: "lg" })}>Processing Your Order</h1>
        <p className="text-gray-600">
          Please wait while we finalize your registration. This usually only
          takes a few seconds.
        </p>
      </div>

      <div className="mx-auto max-w-lg animate-pulse rounded-lg bg-white p-6">
        <div className="mb-4 h-6 w-36 rounded bg-gray-200" />
        <div className="divide-y divide-gray-200">
          {[0, 1].map((i) => (
            <div
              key={i}
              className="flex flex-row items-start justify-between gap-2 py-3"
            >
              <div className="space-y-2">
                <div className="h-4 w-32 rounded bg-gray-200" />
                <div className="h-3 w-24 rounded bg-gray-200" />
              </div>
              <div className="h-4 w-12 rounded bg-gray-200" />
            </div>
          ))}
        </div>
        <div className="mt-4 flex flex-row justify-between border-t border-gray-300 pt-4">
          <div className="h-5 w-20 rounded bg-gray-200" />
          <div className="h-5 w-14 rounded bg-gray-200" />
        </div>
      </div>
    </DefaultLayout>
  );
}

function CompletedIntentView({ invoiceId }: { invoiceId: number | null }) {
  if (invoiceId == null) {
    return (
      <DefaultLayout>
        <div className="mx-auto flex max-w-xl flex-col space-y-6 py-8 text-center">
          <div className="flex justify-center">
            <CheckCircleIcon className="h-16 w-16 text-green-500" />
          </div>
          <h1 className={title({ size: "lg" })}>Payment Successful!</h1>
          <p className="text-gray-600">
            Thank you for your purchase. A confirmation email has been sent to
            your email address.
          </p>
          <div className="mx-auto flex w-full max-w-lg flex-col gap-3">
            <Link
              to="/tournaments"
              className={button({ color: "primary", radius: "full" })}
            >
              Browse Tournaments
            </Link>
            <Link
              to="/account"
              className={button({ variant: "outline", radius: "full" })}
            >
              Go to Account
            </Link>
          </div>
        </div>
      </DefaultLayout>
    );
  }

  return <InvoiceSuccess invoiceId={invoiceId} />;
}

function FailedIntentView({ errorMessage }: { errorMessage: string | null }) {
  return (
    <DefaultLayout>
      <div className="mx-auto flex max-w-xl flex-col space-y-6 py-8 text-center">
        <div className="flex justify-center">
          <AlertTriangleIcon className="h-16 w-16 text-amber-500" />
        </div>
        <h1 className={title({ size: "lg" })}>Registration Failed</h1>
        <p className="text-gray-600">
          {errorMessage ||
            "Something went wrong while finalizing your registration. Your card was not charged."}
        </p>

        <div className="mx-auto max-w-lg">
          <Alert
            color="warning"
            title="Need help?"
            description={
              <span>
                Email us at{" "}
                <a
                  href="mailto:info@cbva.com"
                  className="font-medium underline"
                >
                  info@cbva.com
                </a>{" "}
                and include your name so we can look up your payment.
              </span>
            }
          />
        </div>

        <div className="mx-auto flex w-full max-w-lg flex-col gap-3">
          <Link
            to="/account/registrations/checkout"
            className={button({ color: "primary", radius: "full" })}
          >
            Try Again
          </Link>
          <Link
            to="/account"
            className={button({ variant: "outline", radius: "full" })}
          >
            Go to Account
          </Link>
        </div>
      </div>
    </DefaultLayout>
  );
}

// ── Invoice-based success view ──

function InvoiceSuccess({ invoiceId }: { invoiceId: number }) {
  const trpc = useTRPC();
  const {
    data: invoice,
    isLoading,
    isError,
  } = useQuery(trpc.payments.getInvoiceSummary.queryOptions({ invoiceId }));
  const membershipPrice = useMembershipPrice();
  const dateFormatter = useDateFormatter({ timeZone: getDefaultTimeZone() });

  const memberships = invoice?.memberships ?? [];
  const teamRegistrations = invoice?.teamRegistrations ?? [];

  const total =
    memberships.length * (membershipPrice ?? 0) +
    teamRegistrations.reduce((sum, t) => sum + (t.pricePaid ?? 0), 0);

  if (isLoading) {
    return (
      <DefaultLayout>
        <div className="mx-auto flex max-w-xl flex-col space-y-6 py-8 text-center">
          <div className="flex justify-center">
            <CheckCircleIcon className="h-16 w-16 text-green-500" />
          </div>
          <h1 className={title({ size: "lg" })}>Payment Successful!</h1>
          <p className="text-gray-600">
            Thank you for your purchase. A confirmation email has been sent to
            your email address.
          </p>
        </div>

        <div className="mx-auto max-w-lg animate-pulse rounded-lg bg-white p-6">
          <div className="mb-4 h-6 w-36 rounded bg-gray-200" />
          <div className="divide-y divide-gray-200">
            {[0, 1].map((i) => (
              <div
                key={i}
                className="flex flex-row items-start justify-between gap-2 py-3"
              >
                <div className="space-y-2">
                  <div className="h-4 w-32 rounded bg-gray-200" />
                  <div className="h-3 w-24 rounded bg-gray-200" />
                </div>
                <div className="h-4 w-12 rounded bg-gray-200" />
              </div>
            ))}
          </div>
          <div className="mt-4 flex flex-row justify-between border-t border-gray-300 pt-4">
            <div className="h-5 w-20 rounded bg-gray-200" />
            <div className="h-5 w-14 rounded bg-gray-200" />
          </div>
        </div>
      </DefaultLayout>
    );
  }

  if (isError || !invoice) {
    return (
      <DefaultLayout>
        <div className="mx-auto flex max-w-xl flex-col space-y-6 py-8 text-center">
          <div className="flex justify-center">
            <AlertTriangleIcon className="h-16 w-16 text-amber-500" />
          </div>
          <h1 className={title({ size: "lg" })}>Order Details Unavailable</h1>
          <p className="text-gray-600">
            Your payment may have been processed, but we couldn't load your
            order details. Please check your email for a confirmation, or
            contact us if you need help.
          </p>

          <div className="mx-auto max-w-lg">
            <Alert
              color="warning"
              title="Need help?"
              description={
                <span>
                  Email us at{" "}
                  <a
                    href="mailto:info@cbva.com"
                    className="font-medium underline"
                  >
                    info@cbva.com
                  </a>{" "}
                  and include your name so we can look up your payment.
                </span>
              }
            />
          </div>

          <div className="mx-auto flex w-full max-w-lg flex-col gap-3">
            <Link
              to="/account"
              className={button({ color: "primary", radius: "full" })}
            >
              Go to Account
            </Link>
          </div>
        </div>
      </DefaultLayout>
    );
  }

  return (
    <DefaultLayout>
      <div className="mx-auto flex max-w-xl flex-col space-y-6 py-8 text-center">
        <div className="flex justify-center">
          <CheckCircleIcon className="h-16 w-16 text-green-500" />
        </div>
        <h1 className={title({ size: "lg" })}>Payment Successful!</h1>
        <p className="text-gray-600">
          Thank you for your purchase. A confirmation email has been sent to
          your email address.
        </p>
      </div>

      <div className="mx-auto max-w-lg rounded-lg bg-white p-6">
        <h2 className={title({ size: "sm", className: "mb-4" })}>
          Order Summary
        </h2>

        <div className="divide-y divide-gray-200">
          {memberships.map((membership) => (
            <div
              key={`membership-${membership.id}`}
              className="flex flex-row items-start justify-between gap-2 py-3"
            >
              <div>
                <div className="font-medium">Annual Membership</div>
                <div className="text-sm text-gray-600">
                  <ProfileName {...membership.profile} link={false} />
                </div>
              </div>
              {membershipPrice != null && (
                <span className="shrink-0 font-semibold">
                  ${membershipPrice}
                </span>
              )}
            </div>
          ))}

          {teamRegistrations.map((reg) => {
            const { tournamentDivision } = reg;
            const { tournament } = tournamentDivision;

            return (
              <div
                key={`team-${reg.id}`}
                className="flex flex-row items-start justify-between gap-2 py-3"
              >
                <div>
                  <div className="font-medium">
                    {getTournamentDivisionDisplay(tournamentDivision)}{" "}
                    {tournament.venue.slug}:{" "}
                    {dateFormatter.format(
                      parseDate(tournament.date).toDate(getDefaultTimeZone()),
                    )}
                  </div>
                  <div className="text-sm text-gray-600">
                    {reg.team.players.map((player, i) => (
                      <span key={player.profile.id}>
                        {i > 0 && ", "}
                        <ProfileName {...player.profile} link={false} />
                      </span>
                    ))}
                  </div>
                  {reg.status === "waitlisted" && (
                    <div className="mt-1 text-xs font-medium text-amber-600">
                      Waitlisted
                    </div>
                  )}
                </div>
                <span className="shrink-0 font-semibold">
                  ${reg.pricePaid ?? 0}
                </span>
              </div>
            );
          })}
        </div>

        <div className="mt-4 flex flex-row justify-between border-t border-gray-300 pt-4 text-lg font-bold">
          <span>Total Paid</span>
          <span>${total}</span>
        </div>

        <p className="mt-4 text-center text-sm text-gray-500">
          If something doesn't look right, please{" "}
          <a href="mailto:info@cbva.com" className="text-blue-600 underline">
            contact us
          </a>
          .
        </p>

        <div className="mt-6 flex flex-col gap-3">
          <Link
            to="/tournaments"
            className={button({ color: "primary", radius: "full" })}
          >
            Browse Tournaments
          </Link>
          <Link
            to="/account"
            className={button({ variant: "outline", radius: "full" })}
          >
            Go to Account
          </Link>
        </div>
      </div>
    </DefaultLayout>
  );
}
